import { Injectable, OnDestroy } from '@angular/core';
import { LoggerService, NetworkExtensionService } from '@netfoundry-ui/shared/services';
import { BehaviorSubject, defer, Observable, of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { FrontdoorSelectionService } from './frontdoor-selection.service';

/**
 * Which network extensions the selected frontdoor's network carries, such as `HTTP_SHARES` or `IPSEC_TUNNELER`. The
 * frontdoor names its own network, which `NetworkExtensionGuard` cannot use: that reads whatever the Network sub-app was
 * last pointed at. One read per network answers every extension.
 */
@Injectable({ providedIn: 'root' })
export class FrontdoorExtensionAvailabilityService implements OnDestroy {
    /** The extensions on the resolved network. `ALL` when that network could not be read, so every gate fails open. */
    private readonly extensionsSource = new BehaviorSubject<Extensions>(new Set<string>());
    private readonly subscription = new Subscription();

    /** The network the answer in `extensionsSource` is about, or '' when nothing has been read. */
    private resolvedNetworkId = '';

    /** Counts the forgets, so a read that started before one does not store its old answer. */
    private forgets = 0;

    constructor(
        private selection: FrontdoorSelectionService,
        private networkExtensionService: NetworkExtensionService,
        private logger: LoggerService
    ) {
        this.subscription.add(this.selection.current$.subscribe(() => this.refresh()));
        this.subscription.add(
            this.networkExtensionService.forgotten$.subscribe((networkId) => this.onForgotten(networkId))
        );
    }

    /**
     * True while the selected frontdoor's network carries `extension`. Drives the nav items, so it stays false until
     * something has answered rather than appearing and vanishing. {@link check} fails open instead.
     */
    enabled$(extension: string): Observable<boolean> {
        return this.extensionsSource.pipe(
            map((extensions) => isOn(extensions, extension)),
            distinctUntilChanged()
        );
    }

    /** {@link enabled$} for several extensions at once, in the order given. One network answer emits once, not once per extension. */
    enabledEach$(extensions: string[]): Observable<boolean[]> {
        return this.extensionsSource.pipe(
            map((known) => extensions.map((extension) => isOn(known, extension))),
            distinctUntilChanged((a, b) => a.every((on, index) => on === b[index]))
        );
    }

    isEnabled(extension: string): boolean {
        return isOn(this.extensionsSource.value, extension);
    }

    /**
     * Resolve now, for a caller that cannot wait on the stream, such as a route guard. **Fails open**: a frontdoor naming
     * no network and a network that could not be read both answer true, because the server gate is the real one.
     */
    check(extension: string): Observable<boolean> {
        return this.resolve().pipe(map((extensions) => isOn(extensions, extension)));
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    /** The extensions on the selected frontdoor's network, reading the frontdoor list first when nothing has yet. */
    private resolve(): Observable<Extensions> {
        // The listings and forms each load the list in their own ngOnInit, so a guard can run before any of them has.
        if (this.selection.current === null) {
            return this.selection.load().pipe(
                // An empty list is the normal answer for a user with no v3 access, and a failed read is not ours to report.
                catchError(() => of([])),
                switchMap(() => this.resolveSelected())
            );
        }
        return this.resolveSelected();
    }

    /** {@link resolve} once the frontdoor selection is known to have been read. */
    private resolveSelected(): Observable<Extensions> {
        const networkId = this.networkId;
        if (!networkId) {
            return of(ALL);
        }
        return defer(() => {
            const forgets = this.forgets;
            return this.networkExtensionService.enabledExtensions(networkId).pipe(
                map((extensions): Extensions => new Set(extensions)),
                catchError((error) => {
                    this.logger.error(
                        'FrontdoorExtensionAvailabilityService: could not read network extensions',
                        error
                    );
                    return of(ALL);
                }),
                tap((extensions) => {
                    if (forgets !== this.forgets) {
                        return;
                    }
                    this.resolvedNetworkId = networkId;
                    this.extensionsSource.next(extensions);
                })
            );
        });
    }

    /** The network behind the selected frontdoor, or '' while none is selected. */
    private get networkId(): string {
        return this.selection.current?.networkId ?? '';
    }

    /** Re-read after the selection changed. Extensions belong to the network, so another frontdoor on it needs no request. */
    private refresh() {
        const networkId = this.networkId;
        if (!networkId) {
            this.resolvedNetworkId = '';
            this.extensionsSource.next(new Set<string>());
            return;
        }
        if (networkId === this.resolvedNetworkId) {
            return;
        }
        this.resolve().subscribe();
    }

    /** An operator switched an extension on the selected network, so read it again, also when a read is in flight. */
    private onForgotten(networkId: string) {
        const selected = this.networkId;
        if (!selected || (networkId && networkId !== selected)) {
            return;
        }
        this.forgets++;
        this.resolvedNetworkId = '';
        this.refresh();
    }
}

/** Stands for every extension, when the network could not be read. */
const ALL = 'ALL' as const;

type Extensions = ReadonlySet<string> | typeof ALL;

function isOn(extensions: Extensions, extension: string): boolean {
    return extensions === ALL || extensions.has(extension);
}
